/**
 * Bot Status Indicator Component
 * 
 * Shows live status of the bot with:
 * - Idle/Processing state with emoji
 * - Current order being processed
 * - Step progress with details
 * - Stop button to cancel processing
 * - Expandable details panel
 * - Live timer showing elapsed time
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBotStatus } from '../hooks/useOrders';
import { toDate } from '../types';
import { wywolajFunkcje } from '../services/funkcje';
import './BotStatusIndicator.css';

interface BotStatusIndicatorProps {
  compact?: boolean;
}

export function BotStatusIndicator({ compact }: BotStatusIndicatorProps = {}) {
  const { botStatus, loading } = useBotStatus();
  const [showDetails, setShowDetails] = useState(true); // Default expanded
  const [stopping, setStopping] = useState(false);
  const [elapsedTime, setElapsedTime] = useState('0:00');

  // Live timer that updates every second
  useEffect(() => {
    if (!botStatus || botStatus.state !== 'processing' || !botStatus.started_at) {
      setElapsedTime('0:00');
      return;
    }

    const updateTimer = () => {
      const startTime = toDate(botStatus.started_at!);
      const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      setElapsedTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTimer(); // Initial update
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [botStatus?.started_at, botStatus?.state]);

  const handleStopBot = async () => {
    if (!confirm('Czy na pewno chcesz zatrzymać bota? Aktualne zamówienie nie zostanie ukończone.')) {
      return;
    }
    
    setStopping(true);
    try {
      const response = await wywolajFunkcje(
        'stopBot',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to stop bot');
      }
      
      // Status will update via Firestore listener
    } catch (error) {
      console.error('Error stopping bot:', error);
      alert('Nie udało się zatrzymać bota. Spróbuj ponownie.');
    } finally {
      setStopping(false);
    }
  };

  if (loading) {
    if (compact) return <span className="bot-compact loading">🔄</span>;
    return (
      <div className="bot-status-indicator loading">
        <div className="bot-status-main">
          <span className="bot-emoji">🔄</span>
          <span className="bot-text">Ładowanie...</span>
        </div>
      </div>
    );
  }

  if (!botStatus || botStatus.state === 'idle') {
    if (compact) return <span className="bot-compact idle">😴</span>;
    return (
      <div className="bot-status-indicator idle">
        <div className="bot-status-main">
          <span className="bot-emoji">😴</span>
          <div className="bot-info">
            <span className="bot-text">Bot odpoczywa</span>
            <span className="bot-subtitle">Oczekuje na zamówienia</span>
          </div>
        </div>
      </div>
    );
  }

  // Bot is processing
  const progressPercent = botStatus.total_steps 
    ? Math.round((botStatus.step_number || 0) / botStatus.total_steps * 100)
    : 0;

  const itemsProgress = botStatus.items_total && botStatus.items_total > 0
    ? Math.round(((botStatus.items_processed || 0) / botStatus.items_total) * 100)
    : 0;

  if (compact) {
    return (
      <span className="bot-compact processing" title={`Bot: zamówienie #${botStatus.woocommerce_order_id}`}>
        🤖 <span className="bot-compact-progress">{progressPercent}%</span>
      </span>
    );
  }

  return (
    <div className="bot-status-indicator processing expanded">
      <div className="bot-status-header" onClick={() => setShowDetails(!showDetails)}>
        <div className="bot-header-left">
          <span className="bot-emoji working">🤖</span>
          <div className="bot-header-info">
            <span className="bot-text">Bot pracuje</span>
            <span className="bot-order-id">
              Zamówienie #{botStatus.woocommerce_order_id}
            </span>
          </div>
        </div>
        <div className="bot-header-right">
          <span className="bot-toggle">{showDetails ? '▼' : '▶'}</span>
        </div>
      </div>

      {/* Progress bar always visible */}
      <div className="bot-progress-section">
        <div className="bot-progress-info">
          <span className="bot-step-label">Krok {botStatus.step_number || 1}/{botStatus.total_steps || 12}</span>
          <span className="bot-progress-percent">{progressPercent}%</span>
        </div>
        <div className="bot-progress">
          <div 
            className="bot-progress-bar" 
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Current step - always visible */}
      <div className="bot-current-step-section">
        <div className={`bot-step-status-badge ${botStatus.step_status || 'running'}`}>
          {botStatus.step_status === 'running' && '⏳'}
          {botStatus.step_status === 'success' && '✅'}
          {botStatus.step_status === 'error' && '❌'}
        </div>
        <div className="bot-current-step-text">
          {botStatus.current_step || 'Inicjalizacja...'}
        </div>
      </div>

      {/* Expandable details */}
      {showDetails && (
        <div className="bot-details-panel">
          {/* Timer - always visible and prominently displayed */}
          <div className="bot-detail-item timer-item">
            <span className="bot-detail-label">⏱️ Czas pracy:</span>
            <span className="bot-detail-value timer-value">{elapsedTime}</span>
          </div>

          {/* Current product being processed */}
          {botStatus.current_product && (
            <div className="bot-detail-item product-item">
              <span className="bot-detail-label">🛒 Aktualny produkt:</span>
              <span className="bot-detail-value">{botStatus.current_product}</span>
            </div>
          )}

          {/* Step details */}
          {botStatus.step_details && (
            <div className="bot-detail-item">
              <span className="bot-detail-label">📝 Szczegóły:</span>
              <span className="bot-detail-value">{botStatus.step_details}</span>
            </div>
          )}

          {/* Last action performed */}
          {botStatus.last_action && (
            <div className="bot-detail-item action-item">
              <span className="bot-detail-label">🔄 Ostatnia akcja:</span>
              <span className="bot-detail-value">{botStatus.last_action}</span>
            </div>
          )}
          
          {/* Products progress with full bar */}
          {botStatus.items_total && botStatus.items_total > 0 && (
            <div className="bot-detail-item products-item">
              <span className="bot-detail-label">📦 Produkty:</span>
              <div className="bot-items-progress">
                <span className="bot-items-count">
                  {botStatus.items_processed || 0} / {botStatus.items_total} ({itemsProgress}%)
                </span>
                <div className="bot-items-bar">
                  <div 
                    className="bot-items-fill"
                    style={{ width: `${((botStatus.items_processed || 0) / botStatus.items_total) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Current URL if available */}
          {botStatus.current_url && (
            <div className="bot-detail-item url-item">
              <span className="bot-detail-label">🌐 Strona:</span>
              <span className="bot-detail-value url-value">{botStatus.current_url}</span>
            </div>
          )}

          {/* Error message if any */}
          {botStatus.error_message && (
            <div className="bot-detail-item error-item">
              <span className="bot-detail-label">❌ Błąd:</span>
              <span className="bot-detail-value error-value">{botStatus.error_message}</span>
            </div>
          )}

          <div className="bot-actions">
            <Link 
              to={`/orders/${botStatus.current_order_id}`}
              className="bot-action-btn view-btn"
            >
              👁️ Zobacz szczegóły
            </Link>
            <button 
              onClick={handleStopBot}
              className="bot-action-btn stop-btn"
              disabled={stopping}
            >
              {stopping ? '⏳ Zatrzymuję...' : '🛑 Zatrzymaj bota'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
